import React, { useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Select, EmptyState } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";

const LIFECYCLES = [
  { value: "onboarding", label: "Onboarding" },
  { value: "active", label: "Active" },
  { value: "run_off", label: "Run-off" },
  { value: "closed", label: "Closed" },
];

const HIRE_OPTIONS = [
  { value: "unavailable", label: "Unavailable" },
  { value: "on_request", label: "On request" },
  { value: "required", label: "Required" },
];

// Toggle-style capabilities (enable / disable only).
const MODULES = [
  { key: "accident", label: "Accident" },
  { key: "disposal_returns", label: "Disposal / returns" },
];

async function loadConfig() {
  const [{ data: customers }, { data: caps }] = await Promise.all([
    supabase.from("customers").select("id, name, customer_code, lifecycle_status, dc_code").order("name"),
    supabase.from("customer_capabilities").select("customer_id, capability_key, enabled, config"),
  ]);
  const byCust = {};
  (caps || []).forEach((c) => { (byCust[c.customer_id] = byCust[c.customer_id] || {})[c.capability_key] = c; });
  return { customers: customers || [], caps: byCust };
}

function hirePolicyOf(cap) {
  return cap?.config?.hire_policy || (cap?.enabled ? "on_request" : "unavailable");
}

export default function CustomerConfig() {
  const { data, loading, reload } = useAsync(loadConfig, []);
  const [busy, setBusy] = useState("");

  if (loading) return <StaffShell><Spinner /></StaffShell>;
  const { customers, caps } = data;

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); await reload(); toast.success("Saved."); }
    catch (e) { toast.error(e.message || "Couldn't save that change."); }
    finally { setBusy(""); }
  };

  const setLifecycle = (cid, status) => run(`${cid}-lc`, () => api.adminSetLifecycle(cid, status));
  const setHire = (cid, policy) =>
    run(`${cid}-hire`, () => api.adminSetCapability(cid, {
      capability_key: "daily_hire", enabled: policy !== "unavailable", config: { hire_policy: policy },
    }));
  const toggleModule = (cid, key, enabled) =>
    run(`${cid}-${key}`, () => api.adminSetCapability(cid, { capability_key: key, enabled: !enabled }));
  const saveSettings = (cid, payload) => run(`${cid}-set`, () => api.adminSetSettings(cid, payload));

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Administration</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-1">Customer setup</h1>
      <p className="text-sm text-muted-foreground mb-5">
        Control each customer's lifecycle status and the service modules they receive. Setting a
        customer to <strong>Run-off</strong> automatically enables Disposal / returns.
      </p>

      {customers.length === 0 ? <EmptyState title="No customers" /> : (
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Customer</th>
                  <th className="px-4 py-2.5 font-medium">DC code</th>
                  <th className="px-4 py-2.5 font-medium">Lifecycle</th>
                  <th className="px-4 py-2.5 font-medium">Daily hire</th>
                  {MODULES.map((m) => <th key={m.key} className="px-4 py-2.5 font-medium">{m.label}</th>)}
                  <th className="px-4 py-2.5 font-medium">OOH hours (open · close · phone)</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => {
                  const cc = caps[c.id] || {};
                  return (
                    <tr key={c.id} className="border-t border-border" data-testid={`cfg-row-${c.customer_code}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{c.name}</div>
                        <div className="text-xs text-muted-foreground vda-reg">{c.customer_code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <input data-testid={`cfg-dccode-${c.customer_code}`} defaultValue={c.dc_code || ""} placeholder="e.g. SEW001C"
                          onBlur={(e) => { if (e.target.value !== (c.dc_code || "")) saveSettings(c.id, { dc_code: e.target.value }); }}
                          className="h-8 w-28 rounded-[6px] border border-[hsl(var(--input))] bg-card px-2 text-sm vda-reg" />
                      </td>
                      <td className="px-4 py-3">
                        <Select data-testid={`cfg-lifecycle-${c.customer_code}`} value={c.lifecycle_status || "active"}
                          disabled={busy === `${c.id}-lc`} onChange={(e) => setLifecycle(c.id, e.target.value)}
                          className="w-auto min-w-[140px]">
                          {LIFECYCLES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Select data-testid={`cfg-hire-${c.customer_code}`} value={hirePolicyOf(cc.daily_hire)}
                          disabled={busy === `${c.id}-hire`} onChange={(e) => setHire(c.id, e.target.value)}
                          className="w-auto min-w-[140px]">
                          {HIRE_OPTIONS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                        </Select>
                      </td>
                      {MODULES.map((m) => {
                        const enabled = !!cc[m.key]?.enabled;
                        return (
                          <td key={m.key} className="px-4 py-3">
                            <button data-testid={`cfg-${m.key}-${c.customer_code}`}
                              disabled={busy === `${c.id}-${m.key}`}
                              onClick={() => toggleModule(c.id, m.key, enabled)}
                              aria-pressed={enabled}
                              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-[6px] text-xs font-medium border transition-colors disabled:opacity-50 ${enabled
                                ? "bg-[hsl(var(--vda-green-bg))] text-primary border-[hsl(var(--primary))]/25"
                                : "bg-card text-muted-foreground border-[hsl(var(--input))]"}`}>
                              {enabled ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                              {enabled ? "On" : "Off"}
                            </button>
                          </td>
                        );
                      })}
                      <td className="px-4 py-3">
                        {(() => { const h = cc.ooh?.config || {}; return (
                          <div className="flex items-center gap-1.5">
                            <input data-testid={`cfg-ooh-open-${c.customer_code}`} defaultValue={h.open || ""} placeholder="08:00"
                              onBlur={(e) => saveSettings(c.id, { ooh_open: e.target.value })}
                              className="h-8 w-16 rounded-[6px] border border-[hsl(var(--input))] bg-card px-2 text-sm vda-reg" />
                            <input data-testid={`cfg-ooh-close-${c.customer_code}`} defaultValue={h.close || ""} placeholder="17:00"
                              onBlur={(e) => saveSettings(c.id, { ooh_close: e.target.value })}
                              className="h-8 w-16 rounded-[6px] border border-[hsl(var(--input))] bg-card px-2 text-sm vda-reg" />
                            <input data-testid={`cfg-ooh-phone-${c.customer_code}`} defaultValue={h.phone || ""} placeholder="i247 tel"
                              onBlur={(e) => saveSettings(c.id, { ooh_phone: e.target.value })}
                              className="h-8 w-28 rounded-[6px] border border-[hsl(var(--input))] bg-card px-2 text-sm vda-reg" />
                          </div>
                        ); })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </StaffShell>
  );
}
